import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Package, BookOpen, ArrowRight, Loader } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading, success, pending, error
  const [orderData, setOrderData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async (attempt = 0) => {
    if (attempt >= 6) {
      setStatus("pending");
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = res.data;
      if (data.payment_status === "paid") {
        setOrderData(data);
        setStatus("success");
        await refreshUser(); // refresh user to get digital access
      } else if (data.status === "expired") {
        setStatus("error");
      } else {
        setAttempts(attempt + 1);
        setTimeout(() => pollStatus(attempt + 1), 2500);
      }
    } catch {
      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(attempt + 1), 2500);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FDFBF7" }} data-testid="order-success-loading">
        <div className="text-center space-y-4">
          <Loader className="w-12 h-12 text-[#2F5233] animate-spin mx-auto" />
          <h2 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>Processing your payment...</h2>
          <p className="text-[#5A5A5A]">Please wait, this may take a moment. Don't close this window.</p>
          {attempts > 2 && <p className="text-xs text-[#5A5A5A]">Still checking... ({attempts}/6)</p>}
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FDFBF7" }} data-testid="order-error">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl text-red-500">!</span>
          </div>
          <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Payment Issue</h2>
          <p className="text-[#5A5A5A] mb-6">Your payment session expired or could not be verified. No charge was made.</p>
          <Link to="/shop" className="bg-[#2F5233] text-white px-8 py-3 rounded-full font-bold hover:bg-[#1E3A21] transition-all">
            Try Again
          </Link>
        </div>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FDFBF7" }} data-testid="order-pending">
        <div className="text-center max-w-md">
          <Loader className="w-12 h-12 text-[#D2691E] animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Payment Processing</h2>
          <p className="text-[#5A5A5A] mb-4">Your payment is being verified. Check your email for confirmation, or contact us if you have concerns.</p>
          <Link to="/" className="text-[#2F5233] font-bold hover:underline">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16" style={{ backgroundColor: "#FDFBF7" }} data-testid="order-success-page">
      <div className="max-w-2xl mx-auto px-4 text-center">
        <div className="w-24 h-24 bg-[#B2D3C2] rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse-glow">
          <CheckCircle className="w-12 h-12 text-[#2F5233]" />
        </div>

        <h1 className="text-5xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
          Order Confirmed!
        </h1>
        <p className="text-xl text-[#5A5A5A] mb-2">Thank you for joining the Gift it Forward Movement!</p>
        {orderData?.order_number && (
          <p className="text-sm text-[#5A5A5A] mb-8">
            Order Number: <strong className="text-[#2F5233]" data-testid="order-number">{orderData.order_number}</strong>
          </p>
        )}

        <div className="bg-white rounded-3xl p-8 border border-[#E6E2D3] mb-8 text-left space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#B2D3C2] rounded-full flex items-center justify-center flex-shrink-0">
              <Package className="w-5 h-5 text-[#2F5233]" />
            </div>
            <div>
              <h3 className="font-bold text-[#1A1A1A]">Physical Books</h3>
              <p className="text-[#5A5A5A] text-sm">Your books will be shipped within 2-3 business days. You'll receive a tracking number via email.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#B2D3C2] rounded-full flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-[#2F5233]" />
            </div>
            <div>
              <h3 className="font-bold text-[#1A1A1A]">Digital Book Access</h3>
              <p className="text-[#5A5A5A] text-sm">If you purchased the digital book, it's now available in your library. Log in to read it instantly on any device.</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/my-library" className="flex items-center justify-center gap-2 bg-[#2F5233] text-white px-8 py-4 rounded-full font-bold hover:bg-[#1E3A21] transition-all hover:scale-105" data-testid="go-to-library-btn">
            <BookOpen className="w-5 h-5" /> Go to My Library
          </Link>
          <Link to="/shop" className="flex items-center justify-center gap-2 border-2 border-[#2F5233] text-[#2F5233] px-8 py-4 rounded-full font-bold hover:bg-[#2F5233]/5 transition-all">
            Continue Shopping <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
